import { Injectable } from '@angular/core';
import { Plugins } from '@capacitor/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthenticationFacade } from '@symphony-solo/domain/authentication/src/lib/+state/authentication.facade';
import { PaymentCardsFacade } from '@symphony-solo/domain/payment-cards/src/lib/+state/payment-cards.facade';
import { ToastFacade } from '@symphony-solo/domain/toast/src';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ErrorCode } from '@symphony-solo/shared/error-handling/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { WebsitePageNamesEnum } from '../enums';
import { LoyaltyWebsiteService } from '../services';
import {
  noClickAndCollectPermissionAction,
  openAddCardAction,
  openClickeAndCollectWebsiteAction,
  openPublicWebsitePageAction,
  openWebsitePageAction,
  openWebsitePageErrorAction,
  openWebsiteSuccessAction,
} from './loyalty-website.actions';
import { LoyaltyWebsiteFacade } from './loyalty-website.facade';
const { Browser } = Plugins;

@Injectable()
export class LoyaltyWebsiteEffects {
  constructor(
    private action$: Actions,
    private loyaltyWebsiteService: LoyaltyWebsiteService,
    private authenticationFacade: AuthenticationFacade,
    private toastFacade: ToastFacade,
    private paymentCardsFacade: PaymentCardsFacade,
    private loyaltyFacade: LoyaltyWebsiteFacade
  ) {}

  protected openWebPage$ = createEffect(() =>
    this.action$.pipe(
      ofType(openWebsitePageAction),
      switchMap((payload) => {
        Browser.removeAllListeners();
        return this.loyaltyWebsiteService.getWebsiteUrl(payload.pageName).pipe(
          tap(async (url) => {
            if (payload.pageName === WebsitePageNamesEnum.LoyaltyPass) {
              url.value += '&nhdr=1';
            }

            Browser.addListener('browserPageLoaded', () => {
              this.loyaltyFacade.setPageOpened();
            });
            await Browser.open({ url: url.value });
          }),
          map(() => openWebsiteSuccessAction({ pageName: payload.pageName })),
          catchError((error) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.General)
              .addFailure(new AppStateFailureItemWithErrorCode('Could not open web page', ErrorCode.CouldNotOpenWebview))
              .build();
            return of(openWebsitePageErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected openPublicWebPage$ = createEffect(() =>
    this.action$.pipe(
      ofType(openPublicWebsitePageAction),
      switchMap((payload) => {
        Browser.removeAllListeners();
        return this.loyaltyWebsiteService.getPublicWebsiteUrl(payload.pageName).pipe(
          tap(async (url) => {
            Browser.addListener('browserPageLoaded', () => {
              this.loyaltyFacade.setPageOpened();
            });
            // we need to replace all ocurrences. this extra \" are added on deserialization of text response
            const trimmedUrl = url.replace(/\"/g, '');
            await Browser.open({ url: trimmedUrl });
          }),
          map(() => openWebsiteSuccessAction({ pageName: payload.pageName })),
          catchError((error) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.General)
              .addFailure(new AppStateFailureItemWithErrorCode('Could not open web page', ErrorCode.CouldNotOpenWebview))
              .build();
            return of(openWebsitePageErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected openAddCard$ = createEffect(() =>
    this.action$.pipe(
      ofType(openAddCardAction),
      switchMap(({ flow }) => {
        Browser.removeAllListeners();
        return this.loyaltyWebsiteService.getWebsiteUrl(WebsitePageNamesEnum.PaymentCards).pipe(
          tap(async (url) => {
            Browser.addListener('browserPageLoaded', () => {
              this.loyaltyFacade.setPageOpened();
            });
            Browser.addListener('browserFinished', () => {
              this.paymentCardsFacade.loadPaymentCards(flow);
            });
            await Browser.open({ url: url.value });
          }),
          map(() => openWebsiteSuccessAction({ pageName: WebsitePageNamesEnum.PaymentCards })),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.General)
              .addFailure(new AppStateFailureItemWithErrorCode('Could not open web page', ErrorCode.CouldNotOpenWebview))
              .build();
            return of(openWebsitePageErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected openClickAndCollect$ = createEffect(() =>
    this.action$.pipe(
      ofType(openClickeAndCollectWebsiteAction),
      withLatestFrom(this.authenticationFacade.accountDetails$),
      switchMap(([_, accountDetails]) => {
        Browser.removeAllListeners();
        if (accountDetails && accountDetails.clickCollectAllowed) {
          return this.loyaltyWebsiteService.getWebsiteUrl(WebsitePageNamesEnum.ClickAndCollect_Sites).pipe(
            tap(async (url) => {
              Browser.addListener('browserPageLoaded', () => {
                this.loyaltyFacade.setPageOpened();
              });
              await Browser.open({ url: url.value });
            }),
            map(() => {
              return openWebsiteSuccessAction({ pageName: WebsitePageNamesEnum.ClickAndCollect_Sites });
            }),
            catchError(() => {
              const failure = new AppStateFailureBuilder()
                .setFailureType(FailureType.General)
                .addFailure(new AppStateFailureItemWithErrorCode('Could not open web page', ErrorCode.CouldNotOpenWebview))
                .build();
              return of(openWebsitePageErrorAction({ failure }));
            })
          );
        }
        return of(noClickAndCollectPermissionAction());
      })
    )
  );

  protected noClickAndCollectPermission$ = createEffect(() =>
    this.action$.pipe(
      ofType(noClickAndCollectPermissionAction),
      tap(() => {
        this.toastFacade.showToastWarningMessage('Oh-no... Pre-Order is not enabled yet, check back again soon.');
      }),
      map(() => {
        const failure = new AppStateFailureBuilder()
          .setFailureType(FailureType.General)
          .addFailure(new AppStateFailureItemWithErrorCode('Could not open web page', ErrorCode.CouldNotOpenWebview))
          .build();
        return openWebsitePageErrorAction({ failure });
      })
    )
  );

  protected error$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(openWebsitePageErrorAction),
        tap(() => {
          Browser.removeAllListeners();
        })
      ),
    {
      dispatch: false,
    }
  );
}
